import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { UserService } from "@/lib/services/userService";
import { toast } from "@/hooks/use-toast";
import { ArrowLeft, Smartphone, Shield } from "lucide-react";

const UserLogin = () => {
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSendOTP = () => {
    if (!mobile || mobile.length < 10) {
      toast({
        title: "Invalid Mobile Number",
        description: "Please enter a valid 10-digit mobile number",
        variant: "destructive",
      });
      return;
    }

    if (!username.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name",
        variant: "destructive",
      });
      return;
    }

    // For demo, any mobile number works
    setOtpSent(true);
    toast({
      title: "OTP Sent",
      description: `OTP sent to ${mobile}. Use 1234 for demo.`,
    });
  };

  const handleVerifyOTP = async () => {
    if (otp !== "1234") {
      toast({
        title: "Invalid OTP",
        description: "Please enter the correct OTP (1234 for demo)",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Create or get user with phone number and username
      const user = await UserService.createOrGetUser(mobile, username);

      if (!user) {
        toast({
          title: "Login Failed",
          description: "Unable to create or retrieve user account",
          variant: "destructive",
        });
        return;
      }

      const authUser = {
        ...user,
        type: "user" as const,
        name: username,
      };

      login(authUser);
      navigate("/user-dashboard");
      toast({
        title: "Login Successful",
        description: "Welcome to Municipal Turf Booking Portal!",
      });
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Login Failed",
        description: "An error occurred during login. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          {/* <Link 
            to="/" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link> */}

          <div className="flex justify-center mb-4">
            <div className="text-center mb-4 flex flex-col items-center"> 

              <img
                src="/logo-placeholder.png"
                alt="Municipal Corporation Logo"
                className="h-20 w-20 mb-4"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-blue-900 mb-2">
            Thoothukudi Corporation
          </h1>
          <h2 className="text-xl font-semibold text-green-700 mb-2">
            Turf Booking Portal
          </h2>
          <h1 className="text-3xl font-bold text-blue-900 mb-2">User Login</h1>
        </div>

        {/* Login Card */}
        <Card className="shadow-xl border-0">
          <CardHeader>
            <CardTitle className="text-center text-xl text-gray-900">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-center">
              Login with your mobile number and OTP
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="username"
                className="text-sm font-medium text-gray-700"
              >
                Full Name
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your full name"
                value={username}
                onChange={(e) => {
                  const input = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(input)) {
                    setUsername(input);
                  }
                }}
                disabled={otpSent || loading}
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="mobile"
                className="text-sm font-medium text-gray-700"
              >
                Mobile Number
              </Label>
              <div className="relative">
                <Smartphone className="absolute left-3 top-4 h-4 w-4 text-gray-400" />
                <Input
                  id="mobile"
                  type="tel"
                  placeholder="Enter 10-digit mobile number"
                  value={mobile}
                  onChange={(e) => {
                    const input = e.target.value;
                    if (/^\d{0,10}$/.test(input)) {
                      setMobile(input);
                    }
                  }}
                  disabled={otpSent || loading}
                  inputMode="numeric"
                  className="h-12 pl-10"
                />
              </div>
            </div>

            {!otpSent ? (
              <Button
                onClick={handleSendOTP}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-300"
                disabled={loading}
              >
                Send OTP
              </Button>
            ) : (
              <>
                <div className="space-y-2">
                  <Label
                    htmlFor="otp"
                    className="text-sm font-medium text-gray-700"
                  >
                    Enter OTP
                  </Label>
                  <div className="relative">
                    <Shield className="absolute left-3 top-4 h-4 w-4 text-gray-400" />
                    <Input
                      id="otp"
                      type="text"
                      placeholder="Enter 4-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      maxLength={4}
                      disabled={loading}
                      className="h-12 pl-10 text-center text-lg tracking-wider"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handleVerifyOTP}
                    className="w-full h-12 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transition-all duration-300"
                    disabled={loading}
                  >
                    {loading ? "Verifying..." : "Verify OTP & Login"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOtpSent(false);
                      setOtp("");
                      setUsername("");
                    }}
                    className="w-full h-12 border-gray-200 hover:bg-gray-50"
                    disabled={loading}
                  >
                    Change Mobile Number
                  </Button>
                </div>
              </>
            )}

            {/* Demo Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">
                    Demo Instructions
                  </h4>
                  <p className="text-xs text-blue-700">
                    Use OTP: <span className="font-mono font-bold">1234</span>{" "}
                    for demo login
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        {/* <div className="text-center mt-6 text-sm text-gray-500">
          <p>
            Need admin access?{" "}
            <Link
              to="/admin-login"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Login as Admin
            </Link>
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default UserLogin;
